import React, { useMemo } from 'react'
import clsx from 'clsx'
import { toDateKey, formatDayLabel } from '../allocationUtils'

/**
 * Returns relative day context badge (e.g. "Today", "Yesterday", "Tomorrow", "In 3 days")
 */
function getRelativeDayInfo(date) {
  if (!date) return null
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffTime = d.getTime() - today.getTime()
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return {
      label: 'Today',
      isToday: true,
      badgeClass: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/25',
    }
  }
  if (diffDays === -1) {
    return {
      label: 'Yesterday',
      isToday: false,
      badgeClass: 'bg-surface-variant text-on-surface-variant border-outline-variant/30',
    }
  }
  if (diffDays === 1) {
    return {
      label: 'Tomorrow',
      isToday: false,
      badgeClass: 'bg-primary/10 text-primary border-primary/25',
    }
  }
  if (diffDays > 1 && diffDays <= 7) {
    return {
      label: `In ${diffDays}d`,
      isToday: false,
      badgeClass: 'bg-surface-container-high text-outline border-outline-variant/20',
    }
  }
  if (diffDays < -1 && diffDays >= -7) {
    return {
      label: `${Math.abs(diffDays)}d ago`,
      isToday: false,
      badgeClass: 'bg-surface-container-high text-outline border-outline-variant/20',
    }
  }
  return null
}

/**
 * DayNavBar
 * ----------
 * Refined, modern day navigation bar for Real Availability (Jobs) mode.
 * - Grouped [ Today ] + [ ‹ | › ] navigation controls.
 * - Single hero date pill with embedded native calendar picker (no duplicated date strings or clunky inputs).
 * - Temporal context badge ("Today", "Yesterday", "Tomorrow", etc.).
 */
export default function DayNavBar({ selectedDay, onPrev, onToday, onNext, onDayChange }) {
  const isToday = toDateKey(selectedDay) === toDateKey(new Date())
  const relativeInfo = useMemo(() => getRelativeDayInfo(selectedDay), [selectedDay])

  return (
    <div className="flex items-center gap-2.5 flex-wrap">
      {/* Navigation Group: Today + Chevrons */}
      <div className="flex items-center gap-1.5">
        {/* Today Quick Jump */}
        <button
          type="button"
          onClick={onToday}
          className={clsx(
            'px-3 py-1.5 text-xs font-bold rounded-xl transition-all shadow-2xs cursor-pointer flex items-center gap-1.5',
            isToday
              ? 'bg-primary text-on-primary shadow-xs ring-2 ring-primary/20'
              : 'bg-surface hover:bg-surface-container-high text-on-surface-variant hover:text-primary border border-outline-variant/30'
          )}
          title="Jump to Today"
        >
          {isToday && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
          <span>Today</span>
        </button>

        {/* Previous / Next segmented pair */}
        <div className="inline-flex rounded-xl border border-outline-variant/30 bg-surface p-0.5 shadow-2xs">
          <button
            type="button"
            onClick={onPrev}
            className="p-1.5 text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high rounded-lg transition-colors flex items-center justify-center cursor-pointer"
            title="Previous Day"
          >
            <span className="material-symbols-outlined text-base leading-none">chevron_left</span>
          </button>
          <div className="w-px h-3.5 bg-outline-variant/30 my-auto" />
          <button
            type="button"
            onClick={onNext}
            className="p-1.5 text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high rounded-lg transition-colors flex items-center justify-center cursor-pointer"
            title="Next Day"
          >
            <span className="material-symbols-outlined text-base leading-none">chevron_right</span>
          </button>
        </div>
      </div>

      {/* Subtle Divider */}
      <div className="h-5 w-px bg-outline-variant/30 hidden sm:block" />

      {/* Hero Interactive Date Pill with integrated invisible picker */}
      <div
        className="relative group flex items-center gap-2 px-3 py-1.5 bg-surface-container-lowest hover:bg-surface-container-low rounded-xl border border-outline-variant/30 hover:border-primary/40 shadow-2xs transition-all cursor-pointer"
        title="Click to pick a specific date"
      >
        {/* Invisible native date input on top for 100% native picker trigger */}
        <input
          type="date"
          value={toDateKey(selectedDay)}
          onChange={(e) => {
            if (e.target.value) {
              const [y, m, d] = e.target.value.split('-').map(Number)
              onDayChange(new Date(y, m - 1, d))
            }
          }}
          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
          title="Click to choose a date"
        />

        {/* Visual Content */}
        <span className="material-symbols-outlined text-primary text-base group-hover:scale-110 transition-transform select-none">
          calendar_today
        </span>

        <span className="text-xs sm:text-sm font-bold text-on-surface tracking-tight group-hover:text-primary transition-colors select-none">
          {formatDayLabel(selectedDay)}
        </span>

        {/* Temporal Relative Badge */}
        {relativeInfo && (
          <span
            className={clsx(
              'px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider border select-none',
              relativeInfo.badgeClass
            )}
          >
            {relativeInfo.label}
          </span>
        )}

        {/* Downward chevron indicator */}
        <span className="material-symbols-outlined text-sm text-outline/60 group-hover:text-primary transition-colors select-none">
          expand_more
        </span>
      </div>
    </div>
  )
}
